import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './Slider.module.css';
import VideoPlayer from './components/VideoPlayer';
import SlideContent from './components/SlideContent';

const Slider = ({ slides }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);
  const [loadedSlides, setLoadedSlides] = useState(new Set());
  const [vh, setVh] = useState(window.innerHeight * 0.01);
  const videoRefs = useRef([]);
  const preloadTimeout = useRef(null);
  const transitionTimeout = useRef(null);
  const autoPlayInterval = useRef(null);

  // Use slides prop instead of importing categoryImages directly
  const items = slides || [];

  const updateVh = useCallback(() => {
    setVh(window.innerHeight * 0.01);
  }, []);

  useEffect(() => {
    updateVh();
    window.addEventListener('resize', updateVh);
    return () => window.removeEventListener('resize', updateVh);
  }, [updateVh]);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      const isMobileDevice = mobileRegex.test(userAgent.toLowerCase()) || window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleTransition = useCallback((newIndex) => {
    if (isTransitioning) return;
    
    // Clear any existing transition timeout
    if (transitionTimeout.current) {
      clearTimeout(transitionTimeout.current);
    }

    setIsTransitioning(true);
    setLoadedSlides(new Set());
    setActiveIndex(newIndex);
    
    // Use a shorter transition time on mobile
    const transitionTime = isMobile ? 400 : 800;
    transitionTimeout.current = setTimeout(() => setIsTransitioning(false), transitionTime);
  }, [isTransitioning, isMobile]);

  const nextSlide = useCallback(() => {
    handleTransition((activeIndex + 1) % items.length);
  }, [activeIndex, items.length, handleTransition]);

  const prevSlide = useCallback(() => {
    handleTransition((activeIndex - 1 + items.length) % items.length);
  }, [activeIndex, items.length, handleTransition]);

  const handleSlideLoad = useCallback((index) => {
    setLoadedSlides(prev => new Set([...prev, index]));
  }, []);

  // Optimized preloading for mobile
  useEffect(() => {
    const preloadVideos = () => {
      const nextIndex = (activeIndex + 1) % items.length;
      const prevIndex = (activeIndex - 1 + items.length) % items.length;

      // On mobile, only preload the next video
      if (isMobile) {
        if (videoRefs.current[nextIndex]) {
          videoRefs.current[nextIndex].current?.load();
        }
      } else {
        // On desktop, preload both next and previous
        if (videoRefs.current[nextIndex]) {
          videoRefs.current[nextIndex].current?.load();
        }
        if (videoRefs.current[prevIndex]) {
          videoRefs.current[prevIndex].current?.load();
        }
      }
    };

    // Clear any existing timeout
    if (preloadTimeout.current) {
      clearTimeout(preloadTimeout.current);
    }

    // Set a new timeout to preload videos
    // Use shorter timeout on mobile
    const preloadDelay = isMobile ? 500 : 1000;
    preloadTimeout.current = setTimeout(preloadVideos, preloadDelay);

    return () => {
      if (preloadTimeout.current) clearTimeout(preloadTimeout.current);
    };
  }, [activeIndex, items.length, isMobile]);

  // Auto-play functionality (enable on both desktop and mobile)
  useEffect(() => {
    if (isTransitioning) {
      if (autoPlayInterval.current) {
        clearInterval(autoPlayInterval.current);
      }
      return;
    }

    autoPlayInterval.current = setInterval(() => {
      nextSlide();
    }, isMobile ? 6000 : 5000);

    return () => {
      if (autoPlayInterval.current) {
        clearInterval(autoPlayInterval.current);
      }
    };
  }, [isTransitioning, nextSlide, isMobile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preloadTimeout.current) clearTimeout(preloadTimeout.current);
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
      if (autoPlayInterval.current) clearInterval(autoPlayInterval.current);
    };
  }, []);

  const onTouchStart = (e) => {
    if (isTransitioning) return;
    if (!e.touches || e.touches.length === 0) return;
    setTouchStart(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
    setTouchEnd(null);
    setTouchEndY(null);
  };

  const onTouchMove = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    setTouchEnd(e.touches[0].clientX);
    setTouchEndY(e.touches[0].clientY);
  };

  const onTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return;
    const deltaX = touchStart - touchEnd;
    const deltaY = (touchStartY ?? 0) - (touchEndY ?? 0);
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const threshold = 40; // px
    // Only trigger when horizontal swipe is dominant
    if (absX > absY && absX > threshold) {
      if (deltaX > 0) {
        // swipe left -> next
        nextSlide();
      } else {
        // swipe right -> prev
        prevSlide();
      }
    }
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartY(null);
    setTouchEndY(null);
  };

  return (
    <div 
      className={styles.container}
      style={{ '--vh': `${vh}px` }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {items.map((slide, index) => {
        const isActive = index === activeIndex;
        const isNext = index === (activeIndex + 1) % items.length;
        const isPrev = index === (activeIndex - 1 + items.length) % items.length;
        const isLoaded = loadedSlides.has(index);
        // On mobile, only load active and next slide
        const shouldLoad = isMobile ? (isActive || isNext) : (isActive || isNext || isPrev);

        return (
          <div
            key={index}
            className={`${styles.slide} ${isActive ? styles.active : ''} ${
              isNext ? styles.next : ''
            } ${isPrev ? styles.prev : ''}`}
          >
            {shouldLoad && (
              <VideoPlayer
                key={index + '-' + (isActive ? 'active' : 'inactive')}
                ref={el => videoRefs.current[index] = el}
                src={slide.videoUrl}
                webm={slide.webmUrl}
                mobileSrc={slide.mobileImage}
                mobileWebm={slide.mobileWebm}
                poster={slide.poster}
                isActive={isActive}
                onLoad={() => handleSlideLoad(index)}
              />
            )}
            <div className={styles.gradientOverlay} />
            <SlideContent
              title={slide.title}
              description={slide.description}
              category={slide.category}
              isLoaded={isLoaded}
            />
          </div>
        );
      })}

      {/* Hide controls on mobile */}
      {!isMobile && (
        <div className={styles.controls}>
          <button 
            className={`${styles.controlButton} ${styles.prevButton}`}
            onClick={prevSlide}
            aria-label="Previous slide"
            disabled={isTransitioning}
          >
            <span className={styles.arrow}>←</span>
          </button>
          <button 
            className={`${styles.controlButton} ${styles.nextButton}`}
            onClick={nextSlide}
            aria-label="Next slide"
            disabled={isTransitioning}
          >
            <span className={styles.arrow}>→</span>
          </button>
        </div>
      )}

      {/* Finger swipe hint for mobile */}
      {isMobile && (
        <div className={styles.fingerHint}>
          <i className="fas fa-hand-point-left"></i>
        </div>
      )}

      <div className={styles.pagination}>
        {items.map((_, index) => (
          <button
            key={index}
            className={`${styles.paginationDot} ${
              index === activeIndex ? styles.active : ''
            }`}
            onClick={() => {
              if (!isTransitioning) {
                handleTransition(index);
              }
            }}
            aria-label={`Go to slide ${index + 1}`}
            disabled={isTransitioning}
          />
        ))}
      </div>
    </div>
  );
};

export default Slider; 